export interface TerminalConfig {
  apiBase: string
  vgsVaultId: string
  vgsEnvironment: string
  cardFields: 'vgs' | 'mock'
}

const TERMINAL_ID = /^[A-Za-z0-9_-]{1,64}$/

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * Reads the build-time configuration.
 * @param env - `import.meta.env`, or a stand-in in tests.
 * @returns The configuration.
 * @throws When a required value is missing or the API base is not an origin.
 */
export function readConfig(env: Record<string, unknown>): TerminalConfig {
  const apiBase = text(env.VITE_TERMINAL_API_BASE)
  const cardFields = env.VITE_CARD_FIELDS === 'mock' ? 'mock' : 'vgs'
  const vgsVaultId = text(env.VITE_VGS_VAULT_ID)
  const vgsEnvironment = text(env.VITE_VGS_ENV)
  let parsed: URL
  try {
    parsed = new URL(apiBase)
  } catch {
    throw new Error('VITE_TERMINAL_API_BASE must be an origin')
  }
  if (parsed.origin !== apiBase) {
    throw new Error('VITE_TERMINAL_API_BASE must be an origin')
  }
  if (cardFields === 'vgs' && (!vgsVaultId || !vgsEnvironment)) {
    throw new Error('VITE_VGS_VAULT_ID and VITE_VGS_ENV are required')
  }
  return { apiBase, vgsVaultId, vgsEnvironment, cardFields }
}

/**
 * Reads the terminal id from a `/t/:terminalId` path.
 * @param pathname - `location.pathname`.
 * @returns The terminal id, or null for any other path.
 */
export function terminalIdFromPath(pathname: string): string | null {
  const match = /^\/t\/([^/]+)\/?$/.exec(pathname)
  if (!match) return null
  const id = match[1]
  return TERMINAL_ID.test(id) ? id : null
}
